import type { ReactNode } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';

type Props = {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  /** Extra links/actions to the left of theme + FAQ (e.g. Contact support) */
  endExtras?: ReactNode;
  showContactSupport?: boolean;
};

export function InterviewPublicAppBar({ title, subtitle, icon, endExtras, showContactSupport }: Props) {
  const { theme, toggleTheme } = useTheme();

  /** Brand purple matches `--ggj-logo-mark-bg` / app shell (visible strip — subtle gradients alone were easy to miss). */
  return (
    <header className="shrink-0 border-b border-violet-200/90 bg-gradient-to-b from-violet-50/95 to-white backdrop-blur-md dark:border-violet-900/50 dark:from-violet-950/50 dark:to-slate-950/95">
      <div
        className="h-1.5 w-full bg-[color:var(--ggj-logo-mark-bg,#8b80f9)]"
        aria-hidden
      />
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 shadow-sm dark:bg-sky-500/15 dark:text-sky-300">
            {icon}
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-900 dark:text-slate-50">{title}</h1>
            {subtitle ? (
              <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
          {endExtras}
          <button
            type="button"
            onClick={() => toggleTheme()}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
          </button>
          <a
            href="https://support.google.com/chrome/answer/2693767"
            target="_blank"
            rel="noreferrer"
            className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
          >
            FAQ
          </a>
          {showContactSupport ? (
            <a href="mailto:support" className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200">
              Contact support
            </a>
          ) : null}
        </div>
      </div>
    </header>
  );
}
